import * as React from 'react';

import { calculateAdjustedPrices, ICalculateAdjustedPricesResult } from './utils/calculateAdjustedPrices';

const DEFAULT_PRICES_MIN_GAP = 1000;

export interface IUsePriceCorrectionParams {
  pricesMinGap?: number;
}

export interface ICalculateCorrectionParams {
  newMin: number | null;
  newMax: number | null;
  isMinChanged: boolean;
}

export interface ICalculateCorrectionResult {
  adjustedMin: number | null;
  adjustedMax: number | null;
  wasCorrected: boolean;
}

/**
 * Хук для расчёта скорректированных цен с учётом минимального разбега.
 * Возвращает чистую функцию расчёта без side-effects.
 *
 * Используется для:
 * - Мобильных фильтров (с toast уведомлением)
 * - Десктопных фильтров (со статичным hint)
 *
 * @param pricesMinGap - минимальный разбег между ценами (по умолчанию 1000)
 * @returns функция расчёта скорректированных цен
 */
export function usePriceCorrection({ pricesMinGap = DEFAULT_PRICES_MIN_GAP }: IUsePriceCorrectionParams = {}): (
  params: ICalculateCorrectionParams,
) => ICalculateCorrectionResult {
  return React.useCallback(
    ({ newMin, newMax, isMinChanged }: ICalculateCorrectionParams): ICalculateCorrectionResult => {
      const result: ICalculateAdjustedPricesResult = calculateAdjustedPrices({
        min: newMin,
        max: newMax,
        isMinChanged,
        pricesMinGap,
      });

      return {
        adjustedMin: result.adjustedMin,
        adjustedMax: result.adjustedMax,
        wasCorrected: result.needsAdjustment,
      };
    },
    [pricesMinGap],
  );
}
